import { useEffect, useState } from 'react';
import { customerApi } from '../customer_api';
import { formatDate, formatMoney } from '../lib/format';

/**
 * Subset of the API Payment row needed by the customer history page.
 * Mirrors `apps/api/src/db/schema/payments.ts` — see that file for the
 * full type. We intentionally keep this loose (string for `kind` /
 * `source` / `status`) so a future kind/source doesn't break the page.
 */
export interface CustomerPayment {
  id: string;
  flow: string;
  kind: string;
  source: string;
  direction: 'in' | 'out';
  amountMinor: number;
  currency: string;
  status: string;
  externalId: string | null;
  bookingId: string | null;
  customerId: string | null;
  createdAt: string;
}

// Human-readable labels for the source column. Anything unknown falls
// back to the raw slug.
const SOURCE_LABEL: Record<string, string> = {
  stripe_card: 'Card',
  stripe_terminal: 'Terminal',
  cash: 'Cash',
  check: 'Check',
  account_credit: 'Credit',
  manual: 'Manual',
};

// Human-readable labels for the kind column. Used to derive a row
// description.
const KIND_LABEL: Record<string, string> = {
  full: 'Full payment',
  deposit: 'Deposit',
  balance: 'Balance',
  pack_purchase: 'Pack purchase',
  membership_invoice: 'Membership',
  pos_sale: 'POS sale',
  refund: 'Refund',
  saas_invoice: 'Subscription',
};

const STATUS_TONES: Record<string, { bg: string; fg: string }> = {
  succeeded: { bg: 'oklch(0.55 0.12 150 / 0.12)', fg: 'oklch(0.36 0.12 150)' },
  pending: { bg: 'oklch(0.70 0.10 80 / 0.16)', fg: 'oklch(0.40 0.10 80)' },
  failed: { bg: 'oklch(0.55 0.15 30 / 0.14)', fg: 'oklch(0.45 0.15 30)' },
  refunded: { bg: 'oklch(0.55 0.05 250 / 0.10)', fg: 'oklch(0.40 0.05 250)' },
  partially_refunded: { bg: 'oklch(0.55 0.05 250 / 0.10)', fg: 'oklch(0.40 0.05 250)' },
};

function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? { bg: 'oklch(0.92 0.005 80)', fg: 'oklch(0.40 0.005 80)' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.1rem 0.5rem',
        borderRadius: '999px',
        background: tone.bg,
        color: tone.fg,
        fontSize: '0.68rem',
        fontWeight: 500,
        textTransform: 'capitalize',
        letterSpacing: '0.02em',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function PaymentAmount({ payment }: { payment: CustomerPayment }) {
  // direction='in' → positive (money customer paid us)
  // direction='out' → negative (refund back to customer)
  const signed = payment.direction === 'out' ? -payment.amountMinor : payment.amountMinor;
  const colour = payment.direction === 'out' ? 'oklch(0.50 0.15 30)' : 'oklch(0.40 0.12 150)';
  const formatted = formatMoney(Math.abs(signed), payment.currency);
  const display = payment.direction === 'out' ? `-${formatted}` : formatted;
  return (
    <span
      className="num"
      data-direction={payment.direction}
      style={{ fontWeight: 500, color: colour, fontVariantNumeric: 'tabular-nums' }}
    >
      {display}
    </span>
  );
}

export function Payments() {
  const [payments, setPayments] = useState<CustomerPayment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customerApi<{ payments: CustomerPayment[] }>('/api/customer/payments')
      .then((data) => setPayments(data.payments))
      .catch(() => setError('Could not load payments. Please try again.'));
  }, []);

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <span
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          History
        </span>
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 1.5rem',
          }}
        >
          Payments
        </h1>

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}

        {!error && payments === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {payments !== null && payments.length === 0 && (
          <div
            style={{
              marginTop: '2rem',
              padding: '2.5rem 1.5rem',
              borderRadius: '14px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: 0,
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                fontSize: '0.95rem',
              }}
            >
              No payments yet.
            </p>
          </div>
        )}

        {payments !== null && payments.length > 0 && (
          <div
            style={{
              borderTop: '1px solid var(--border, oklch(0.915 0.005 80))',
            }}
          >
            {payments.map((p) => {
              const description = KIND_LABEL[p.kind] ?? p.kind.replace(/_/g, ' ');
              const sourceLabel = SOURCE_LABEL[p.source] ?? p.source.replace(/_/g, ' ');
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.9rem 0',
                    borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{description}</span>
                      <StatusBadge status={p.status} />
                    </div>
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                        marginTop: '0.15rem',
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                      }}
                    >
                      <span className="num">{formatDate(p.createdAt)}</span>
                      <span aria-hidden="true">·</span>
                      <span>{sourceLabel}</span>
                    </div>
                  </div>
                  <PaymentAmount payment={p} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
